'use client';

import { useEffect, useState } from 'react';
import Button from '@components/ui/button';
import Counter from '@components/ui/counter';
import { useNavigate, useParams } from 'react-router-dom';
import useWindowSize from '@utils/use-window-size';
import { useGetPrice } from '@framework/product/use-price';
import { useCart } from '@contexts/cart/cart.context';
import { generateCartItem } from '@utils/generate-cart-item';
import ProductAttributes from '@components/product/product-attributes';
import { toast } from 'react-toastify';
import ThumbnailCarousel from '@components/ui/carousel/thumbnail-carousel';
import Image from '@components/ui/image';
import CartIcon from '@components/icons/cart-icon';
import ProductDetailsTab from '@components/product/product-details/product-tab';
import { useTranslation } from '@i18n/client';
import { useProductBySlugQuery } from '@framework/product/get-product-by-slug';
import ProductSingleDetailsLoader from '@components/ui/loaders/product-single-details-loader';
import { getNameForLanguage, Nullable, ProductBatch } from '@framework/types';
import { getProductImageUrl } from '@framework/product/get-product-image';
import { useAuthContext } from '@contexts/use-auth-context';
import { ProductUnitLabelTranslations } from '@components/product/helpers/TranslationsHelper';
import { KosherIcon } from '@components/product/helpers/KosherIcon';

const ProductSingleDetails: React.FC<{ lang: string }> = ({ lang }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(lang, 'common');
  const { isAuthenticated } = useAuthContext();
  const params = useParams();
  const { slug } = params;
  const { width } = useWindowSize();
  const {
    data: product,
    isLoading,
    isError,
    error,
  } = useProductBySlugQuery(slug as string);
  const { addItemToCart, isInCart, getItemFromCart, isInStock } = useCart();
  const [selectedQuantity, setSelectedQuantity] = useState<number>(1);
  const [selectedBatch, setSelectedBatch] = useState<Nullable<ProductBatch>>();
  const [favorite, setFavorite] = useState<boolean>(false);

  const productImage = product ? getProductImageUrl(product) : '';

  const [addToCartLoader, setAddToCartLoader] = useState<boolean>(false);
  const { price, basePrice, discount } = useGetPrice(
    product,
    selectedBatch,
    'EUR',
  );

  useEffect(() => {
    if (product?.batches.length == 1 && !selectedBatch) {
      setSelectedBatch(product.batches[0]);
    }
  }, [product]);

  if (isError && (error as any)?.status == 404) {
    console.error('product was not found', error);
    navigate(`/${lang}/404`);
  }

  if (isLoading || isError) return <ProductSingleDetailsLoader lang={lang} />;

  const isSelected = product?.batches?.length ? selectedBatch : true;

  const item = generateCartItem(product!, selectedBatch);
  const outOfStock =
    isInCart(item.id, item.batch_id) && !isInStock(item.id, item.batch_id);
  function addToCart() {
    if (!isSelected || !price || product?.quantity == 0) return;
    // to show btn feedback while product carting
    setAddToCartLoader(true);
    setTimeout(() => {
      setAddToCartLoader(false);
    }, 1500);

    const item = generateCartItem(product!, selectedBatch);
    addItemToCart(item, selectedQuantity);
    toast(t('text-added-bag'), {
      progressClassName: 'fancy-progress-bar',
      position: width! > 768 ? 'bottom-right' : 'top-right',
      autoClose: 1500,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }

  return (
    <div className="pt-6 pb-2 md:pt-7">
      <div className="grid-cols-10 lg:grid gap-7 2xl:gap-8">
        <div className="col-span-5 mb-6 xl:col-span-6 md:mb-8 lg:mb-0 relative">
          <div
            className="w-full border border-gray-100 rounded-lg overflow-hidden"
            style={{
              height: '400px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            {!!product?.gallery?.length ? (
              <div className="w-full h-full flex items-center justify-center">
                <ThumbnailCarousel
                  gallery={product?.gallery}
                  thumbnailClassName="xl:w-[700px] 2xl:w-[900px]"
                  galleryClassName="xl:w-[150px] 2xl:w-[170px]"
                  lang={lang}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center w-full h-full">
                <Image
                  src={productImage}
                  alt={product ? getNameForLanguage(product, lang) : ''}
                  style={{
                    maxWidth: '100%',
                    objectFit: 'contain',
                  }}
                  className="max-h-[350px] lg:max-h-none"
                />
              </div>
            )}

            {product && (
              <div className="absolute top-2 right-2 z-10">
                <KosherIcon product={product} />
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col col-span-5 shrink-0 xl:col-span-4 xl:ltr:pl-2 xl:rtl:pr-2">
          <div className="pb-3 lg:pb-5">
            <div className="md:mb-2.5 block -mt-1.5">
              <h2 className="text-lg font-medium transition-colors duration-300 text-brand-dark md:text-xl xl:text-2xl">
                {product ? getNameForLanguage(product, lang) : ''}
              </h2>
            </div>
            <div>
              {t('text-sku')}: {product?.id}
            </div>
            <div>
              {t('text-barcode')}: {product?.barcode ?? '---'}
            </div>
            {product?.unit && (
              <ProductUnitLabelTranslations
                product={product}
                className="text-sm font-medium md:text-15px"
              />
            )}
            {product?.manufacturer_name && (
              <div className="text-sm font-medium md:text-15px">
                {t('text-manufactured-by')} {product.manufacturer_name}{' '}
                {product.origin_country
                  ? `, ${t('text-manufactured-in')} ${product.origin_country}`
                  : ''}
              </div>
            )}
            {product?.kosher_label && (
              <div className="text-sm font-medium md:text-15px">
                {t('Kosher')}: {product.kosher_label}
              </div>
            )}

            {isAuthenticated && (
              <div className="flex items-center mt-5">
                <div className="text-brand-dark font-bold text-base md:text-xl xl:text-[22px]">
                  {price}
                </div>
                {discount && (
                  <>
                    <del className="text-sm text-opacity-50 md:text-15px ltr:pl-3 rtl:pr-3 text-brand-dark ">
                      {basePrice}
                    </del>
                    <span className="inline-block rounded font-bold text-xs md:text-sm bg-brand-tree bg-opacity-20 text-brand-tree uppercase px-2 py-1 ltr:ml-2.5 rtl:mr-2.5">
                      {discount} {t('text-off')}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          {isAuthenticated && product?.batches && (
            <ProductAttributes
              batches={product?.batches}
              selectedBatch={selectedBatch}
              setSelectedBatch={setSelectedBatch}
            />
          )}

          {isAuthenticated && (
            <div className="pb-2">
              {(product?.quantity === 0 || selectedBatch?.quantity === 0) && (
                <div className="text-base text-red-500 whitespace-nowrap">
                  {t('text-out-stock')}
                </div>
              )}
            </div>
          )}

          <div className="pt-1.5 lg:pt-3 xl:pt-4 space-y-2.5 md:space-y-3.5">
            {isAuthenticated && (
              <Counter
                variant="single"
                maxValue={item.stock}
                value={selectedQuantity}
                onIncrement={() => setSelectedQuantity((prev) => prev + 1)}
                onDecrement={() =>
                  setSelectedQuantity((prev) => (prev !== 1 ? prev - 1 : 1))
                }
                onValueChange={setSelectedQuantity}
                editableInput={true}
                disabled={
                  isInCart(item.id, item.batch_id)
                    ? getItemFromCart(item.id, item.batch_id).quantity +
                        selectedQuantity >=
                      Number(item.stock)
                    : selectedQuantity >= Number(item.stock)
                }
                lang={lang}
              />
            )}
            {isAuthenticated && (
              <Button
                onClick={addToCart}
                className="w-full px-1.5"
                disabled={!isSelected && !price && Number(item.stock) > 0}
                loading={addToCartLoader}
              >
                <CartIcon color="#ffffff" className="ltr:mr-3 rtl:ml-3" />
                {t('text-add-to-cart')}
              </Button>
            )}
            <div className="grid gap-2.5"></div>
          </div>
        </div>
      </div>
      <ProductDetailsTab lang={lang} product={product} />
    </div>
  );
};

export default ProductSingleDetails;
